import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Theater, ChevronDown, MapPin } from "lucide-react";
import { fmtShowDate, RECITAL_L, TicketButton, CalendarButtons, Poster } from "./showsUtils";

const Mine = () => <span className="text-xs font-semibold uppercase tracking-wide rounded-full bg-primary text-primary-foreground px-2.5 py-1" data-testid="show-mine">Il tuo corso</span>;

const RecitalRow = ({ x }) => (
  <>
    <div className="flex flex-wrap items-center gap-2">{x.mine && <Mine />}<span className="text-xs uppercase tracking-widest text-muted-foreground">{RECITAL_L[x.kind]}</span></div>
    <p className="text-lg font-semibold break-words">{x.course}</p>
    <p className="text-xl font-bold first-letter:uppercase">{fmtShowDate(x.date)}</p>
    {x.notes && <p className="text-sm whitespace-pre-line break-words">{x.notes}</p>}
    {!x.past && <TicketButton href={x.link} testid={`show-tickets-${x.id}`} />}
    {!x.past && <CalendarButtons ics={x.ics} testid={`show-cal-${x.id}`} title={`${RECITAL_L[x.kind]} · ${x.course}`} date={x.date} details={x.notes} />}
  </>
);

const EventRow = ({ x }) => (
  <>
    {x.mine && <div><Mine /></div>}
    <Poster key={x.poster_url} url={x.poster_url} title={x.title} testid={`show-poster-${x.id}`} />
    <p className="text-lg font-semibold break-words" data-testid={`show-title-${x.id}`}>{x.title}</p>
    {x.description && <p className="text-sm whitespace-pre-line break-words">{x.description}</p>}
    {x.location && <p className="text-sm text-muted-foreground flex items-start gap-1.5 break-words"><MapPin size={15} className="mt-0.5 shrink-0" /> {x.location}</p>}
    <ul className="space-y-3 pt-1">
      {x.performances.map((p) => (
        <li key={p.id} className={`space-y-2 ${p.past ? "opacity-60" : ""}`} data-testid={`show-perf-${p.id}`}>
          <p className="text-xl font-bold first-letter:uppercase">{fmtShowDate(p.date, p.time)}</p>
          {!p.past && <TicketButton href={p.link} testid={`show-perf-tickets-${p.id}`} />}
          {!p.past && <CalendarButtons ics={p.ics} testid={`show-perf-cal-${p.id}`} title={x.title} date={p.date} time={p.time} location={x.location} details={x.description} />}
        </li>
      ))}
    </ul>
  </>
);

// Sezione "Spettacoli": caricata solo all'apertura
export const StudentShows = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const toggle = (o) => {
    setOpen(o);
    if (o && !data) api.get("/me/shows").then((r) => setData(r.data)).catch(() => setData({ items: [], error: true }));
  };
  return (
    <Card className="bg-card border-border" data-testid="my-shows">
      <Collapsible open={open} onOpenChange={toggle}>
        <CollapsibleTrigger data-testid="my-shows-toggle" className="w-full p-5 flex items-center justify-between gap-3 text-left min-h-[64px]">
          <span className="text-2xl flex items-center gap-2"><Theater size={20} className="text-primary shrink-0" /> Spettacoli</span>
          <ChevronDown size={20} className={`text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 sm:px-5 pb-5">
          {!data ? <p className="text-muted-foreground" data-testid="my-shows-loading">Caricamento…</p>
            : data.error ? <p className="text-muted-foreground" data-testid="my-shows-error">Impossibile caricare gli spettacoli. Riprova più tardi.</p>
            : data.items.length === 0 ? <p className="text-muted-foreground" data-testid="my-shows-empty">Non ci sono ancora spettacoli in programma</p> : (
              <ul className="space-y-3" data-testid="my-shows-list">
                {data.items.map((x) => (
                  <li key={x.id} data-testid={`show-${x.id}`} className={`rounded-xl border p-4 space-y-2 ${x.mine ? "border-primary bg-primary/5" : "border-border bg-white"} ${x.past ? "opacity-60" : ""}`}>
                    {x.type === "recital" ? <RecitalRow x={x} /> : <EventRow x={x} />}
                  </li>
                ))}
              </ul>
            )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
